import type { Breakpoint } from '@mui/material'
import React, { createContext, useCallback, useContext, useRef, useState } from 'react'

type ScrollMarginTopOffsetValue = number | Partial<Record<Breakpoint, number>>

interface ScrollNavigationProviderProps {
  children: React.ReactNode
  scrollMarginTopOffset?: ScrollMarginTopOffsetValue
}

export interface NavigationItem {
  id: string
  label: string
}

interface ScrollNavigationContextType {
  activeSection: string
  scrollToSection: (sectionId: string) => void
  registerSectionRef: (id: string, element: HTMLElement | null) => void
  handleSectionInView: (sectionId: string, inView: boolean) => void
  registerNavigationItems: (items: NavigationItem[]) => void
  scrollMarginTopOffset: ScrollMarginTopOffsetValue
}

const ScrollNavigationContext = createContext<ScrollNavigationContextType | null>(null)

export const useScrollNavigation = () => {
  const context = useContext(ScrollNavigationContext)
  if (!context) {
    throw new Error('useScrollNavigation must be used within a ScrollNavigationProvider')
  }
  return context
}

export const ScrollNavigationProvider: React.FC<ScrollNavigationProviderProps> = ({
  children,
  scrollMarginTopOffset = 16, // default value
}) => {
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([])
  const [activeSection, setActiveSection] = useState<string>('')
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const sectionsInView = useRef<Set<string>>(new Set())
  const lastManualClick = useRef<number>(0)

  const registerNavigationItems = useCallback((items: NavigationItem[]) => {
    setNavigationItems(items)
  }, [])

  const handleSectionInView = useCallback(
    (sectionId: string, inView: boolean) => {
      if (inView) {
        sectionsInView.current.add(sectionId)
      } else {
        sectionsInView.current.delete(sectionId)
      }

      // Don't update active section if a manual click happened recently (within 1000ms)
      const timeSinceLastClick = Date.now() - lastManualClick.current
      if (timeSinceLastClick < 1000) {
        return
      }

      // Find the first section in view from our navigation items
      const firstVisibleSection = navigationItems.find(item => sectionsInView.current.has(item.id))
      if (firstVisibleSection) {
        setActiveSection(firstVisibleSection.id)
      } else if (navigationItems.length > 0) {
        setActiveSection(navigationItems[0].id)
      }
    },
    [navigationItems],
  )

  const registerSectionRef = useCallback((id: string, element: HTMLElement | null) => {
    sectionRefs.current[id] = element
  }, [])

  const scrollToSection = useCallback((sectionId: string) => {
    lastManualClick.current = Date.now()
    setActiveSection(sectionId)

    const element = sectionRefs.current[sectionId]
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
    }
  }, [])

  const value = {
    activeSection,
    scrollToSection,
    registerSectionRef,
    handleSectionInView,
    registerNavigationItems,
    scrollMarginTopOffset,
  }

  return <ScrollNavigationContext.Provider value={value}>{children}</ScrollNavigationContext.Provider>
}
